import { useCallback, useEffect, useRef } from 'react';

import type { ValidationError } from '@/lib/graphql/generated/graphql';
import type { RawDealItemForValidation } from '@/lib/workers/validation.worker';
import { ValidationRuleId } from '@/lib/graphql/generated/graphql';
import ValidationWorker from '@/lib/workers/validation.worker?worker&inline';
import {
  backendValidatedProductIdsAtom,
  localValidationErrorsAtom,
} from '@/states/products.state';
import type { ValidationErrorData } from '@appchoose/deal-validation-core';
import { useSetAtom } from 'jotai';

function toValidationError(e: ValidationErrorData): ValidationError {
  return {
    __typename: 'ValidationError',
    id: `local_${e.dealItemId}_${e.ruleId}`,
    ruleId: e.ruleId as ValidationRuleId,
    message: e.message,
    severity: e.severity,
    dealItemId: e.dealItemId,
    createdAt: e.createdAt,
    fixSuggestion: null,
    ruleName: null,
  };
}

export function useValidationWorker() {
  const setLocalErrors = useSetAtom(localValidationErrorsAtom);
  const setBackendConfirmed = useSetAtom(backendValidatedProductIdsAtom);
  const workerRef = useRef<Worker | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const worker = new ValidationWorker();
    workerRef.current = worker;

    worker.onmessage = (
      event: MessageEvent<{
        type: string;
        errors: Record<string, ValidationErrorData[]>;
      }>
    ) => {
      if (event.data.type !== 'VALIDATION_RESULT') return;
      const mapped = new Map<string, ValidationError[]>(
        Object.entries(event.data.errors).map(([id, errors]) => [
          id,
          errors.map(toValidationError),
        ])
      );
      setLocalErrors(mapped);
    };

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      worker.terminate();
      workerRef.current = null;
    };
  }, [setLocalErrors]);

  const validate = useCallback((items: RawDealItemForValidation[]) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      workerRef.current?.postMessage({ type: 'VALIDATE', items });
    }, 150);
  }, []);

  const markBackendConfirmed = useCallback(
    (ids: string[]) => {
      setBackendConfirmed((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
    },
    [setBackendConfirmed]
  );

  return { validate, markBackendConfirmed };
}
